import {createI18n} from 'vue-i18n';
import en from '../../../../../../../resources/lang/en-GB.json';
import {convertValidationPlaceholders} from '../utils';

const FALLBACK_LOCALE = 'en-GB';

/**
 * One lazily fetched chunk per locale file, keyed by its project-root path.
 *
 * Vite resolves the leading "/" from the project root, which neither
 * vite.config.js nor vite.centerstage.config.js overrides. A glob also keeps
 * this safe: ContextLocaleMiddleware pushes an unvalidated "?locale=" into
 * App::setLocale() and from there into <html lang>, so the lookup below must
 * only ever hit a fixed map, never resolve an arbitrary path.
 */
const localeLoaders = import.meta.glob('/resources/lang/*.json', {import: 'default'});

/**
 * en-GB stays bundled and stays the fallback. The secondary locale files carry
 * only a subset of the keys the landing page renders, so a missing key must
 * degrade to English rather than to a raw key (AIRLST-5465).
 */
const i18n = createI18n({
    legacy: false,
    locale: FALLBACK_LOCALE,
    fallbackLocale: FALLBACK_LOCALE,
    messages: {
        [FALLBACK_LOCALE]: convertValidationPlaceholders(en),
    },
    flatJson: true,
});

let pending = null;

/**
 * Switch the instance to the locale the page is rendered in, fetching that
 * locale's chunk on first use.
 *
 * Never rejects: the mount awaits this, and a rejection would leave the guest
 * with a blank registration form or checkout instead of an English one.
 *
 * @returns {Promise<string>} The locale actually in effect.
 */
export function loadPageLocale() {
    pending ??= (async () => {
        try {
            const locale = document.documentElement.lang || FALLBACK_LOCALE;

            if (locale === FALLBACK_LOCALE) {
                return FALLBACK_LOCALE;
            }

            const load = localeLoaders[`/resources/lang/${locale}.json`];

            if (load === undefined) {
                return FALLBACK_LOCALE;
            }

            if (! i18n.global.availableLocales.includes(locale)) {
                // convertValidationPlaceholders reads flat "validation.*" keys, so it has to run
                // before setLocaleMessage nests the map (flatJson), or it silently does nothing.
                i18n.global.setLocaleMessage(locale, convertValidationPlaceholders(await load()));
            }

            i18n.global.locale.value = locale;

            return locale;
        } catch (error) {
            console.error('[i18n] Could not load the page locale, staying on en-GB.', error);

            return FALLBACK_LOCALE;
        }
    })();

    return pending;
}

export default i18n;
